import React from 'react';
import {
  RadioInputGroup,
  RadioInput,
  TextArea,
  Text,
  Link,
  View,
  IconXLine,
  IconButton,
} from '@instructure/ui';
import { useState, useRef, useEffect } from 'react';
/**
 * CheckinItemReason is a component that conditionally renders reasons and comments
 * for a check-in item based on its status. It manages the visibility of reasons
 * and comments, and allows users to select a reason and add comments.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.reasons - An array of reasons to display if applicable.
 * @param {string} props.status - The status of the check-in item which determines
 *                                the visibility of reasons and comments.
 *
 * @returns {JSX.Element} A component that conditionally renders a list of reasons
 *                        and a textarea for comments based on the status.
 */

const CheckinItemReason = ({ reasons, status }) => {
  const commentsRef = useRef(null);
  const [showReasons, setShowReasons] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [requireComment, setRequireComment] = useState(false);

  useEffect(() => {
    if (status && status !== 'accept') {
      setShowReasons(true);
    } else {
      setShowReasons(false);
    }

    if (
      status === 'accept-with-comment' ||
      ((status === 'reject' || status === 'optout') && !reasons?.length)
    ) {
      setRequireComment(true);
      setShowComments(true);
    } else {
      setRequireComment(false);
    }
  }, [status]);

  const handleReasonChange = (value) => {
    if (value === 'other') {
      setShowComments(true);
    } else if (
      commentsRef.current?.value?.length === 0 &&
      showComments &&
      !requireComment
    ) {
      setShowComments(false);
    }
  };

  return (
    <>
      {showReasons && (
        <View as="div" margin="small none none none">
          {status !== 'accept-with-comment' && reasons && reasons.length && (
            <RadioInputGroup
              name="reason"
              onChange={handleReasonChange}
              description="Select a reason"
            >
              {reasons.map((input, index) => (
                <RadioInput key={index} value={input} label={input} />
              ))}
              <RadioInput key="other" value="other" label="Other" />
            </RadioInputGroup>
          )}

          <View as="div" margin="medium none none none">
            {showComments ? (
              <TextArea
                label={
                  <>
                    <Text>Comment</Text>
                    {!requireComment && (
                      <IconButton
                        screenReaderLabel="Close Comment"
                        size="small"
                        withBackground={false}
                        withBorder={false}
                        onClick={() => setShowComments(false)}
                      >
                        <IconXLine />
                      </IconButton>
                    )}
                  </>
                }
                ref={commentsRef}
                margin="medium none none none"
              />
            ) : (
              <Link
                onClick={() => {
                  setShowComments(true);
                  // give it just enough time to show so we can put the cursor in the field..
                  setTimeout(() => {
                    if (commentsRef.current) {
                      commentsRef.current.focus();
                    }
                  }, 100);
                }}
              >
                Add comment
              </Link>
            )}
          </View>

          {status === 'reject' && (
            <View as="div" margin="medium none none none">
              <RadioInputGroup
                name="rejectPlan"
                onChange={handleReasonChange}
                description="How do you want to handle this rejection?"
              >
                <RadioInput
                  key="now"
                  value="now"
                  label="Re-prompt now - resurfaces the prompt to the user right now."
                />
                <RadioInput key="later" value="later" label="Re-prompt later" />
              </RadioInputGroup>
            </View>
          )}
        </View>
      )}
    </>
  );
};

export default CheckinItemReason;
